import {RefCallback, useLayoutEffect, useState} from 'react';

export const PRINT_CAPTION_HEIGHT_CSS_VAR = '--notePartGrid-caption-height';

const PRINT_CAPTION_SELECTOR = '.notePartGrid-caption';

/**
 * Returns null when a caption exists but has no rendered height, which happens when rendering without
 * a layout engine (e.g. nodeshell exports).
 */
function measureCaptionHeight(caption: HTMLElement | null): number | null {
  if (caption == null) {
    return 0;
  }
  const {height} = caption.getBoundingClientRect();
  if (height === 0) {
    return null;
  }
  const marginTop = parseFloat(window.getComputedStyle(caption).marginTop) || 0;
  return Math.ceil(height + marginTop);
}

/**
 * In print preview and PDF export, writes the rendered height of the item's caption (including its top
 * margin) to a CSS variable on the item, so mediocre.less can shrink the image to leave room for it.
 * If the caption can't be measured, the variable is left unset and mediocre.less falls back to an
 * estimated caption height.
 *
 * PDF export prints a static copy of the live page's HTML without running JS, so the value is written
 * as an inline style rather than React state: it is set in the same commit that mounts the caption and
 * is carried into the copy.
 *
 * Returns a ref to attach to the element that contains both the image and the caption.
 */
export default function usePrintCaptionHeight(
  isPrintPreviewView: boolean,
  captionText: string | undefined
): RefCallback<HTMLElement> {
  const [item, setItem] = useState<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (!isPrintPreviewView || item == null) {
      return undefined;
    }
    const caption = item.querySelector<HTMLElement>(PRINT_CAPTION_SELECTOR);
    const update = (): void => {
      const height = measureCaptionHeight(caption);
      if (height == null) {
        item.style.removeProperty(PRINT_CAPTION_HEIGHT_CSS_VAR);
      } else {
        item.style.setProperty(PRINT_CAPTION_HEIGHT_CSS_VAR, `${height}px`);
      }
    };
    update();

    // The first measurement can use a fallback font if web fonts are still loading, and the real font
    // can wrap the caption differently. Export copies the HTML without waiting for fonts, so this
    // re-measurement only reaches the PDF if fonts load before the copy is taken.
    if (caption == null || document.fonts == null) {
      return undefined;
    }
    let isCancelled = false;
    document.fonts.ready.then(() => {
      if (!isCancelled) {
        update();
      }
    });
    return () => {
      isCancelled = true;
    };
  }, [item, isPrintPreviewView, captionText]);

  return setItem;
}
